import { useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Download, FileUp, Plus, Play, Trash2 } from 'lucide-react'
import { lensConfigurationForCandidate } from '../candidateEvidence'
import { makeId } from '../defaults'
import {
  exportCandidatesCsv,
  exportCandidatesJson,
  downloadText,
  parseCandidatesCsv,
  parseCandidatesJson,
} from '../io'
import { evaluateCandidate, syncLpiFromPitch, syncPitchFromLpi } from '../physics'
import type { AppState, CandidateLens } from '../types'
import { HorizontalScrollRegion } from './HorizontalScrollRegion'
import { EmptyState, Field, Metric, ModelBadge, Notice, NumberField, Section, SelectField, StatusBadge, formatNumber } from './ui'

interface CandidatesTabProps {
  state: AppState
  setState: Dispatch<SetStateAction<AppState>>
  selectedId: string | null
  setSelectedId: (id: string | null) => void
  onGoToRay: (candidateId: string) => void
}

const blankCandidate = (): CandidateLens => ({
  id: makeId('candidate'),
  name: 'Untitled candidate',
  manufacturer: '',
  mode: 'thin',
  pitchMm: null,
  lpi: null,
  focalLengthMm: null,
  focalReference: 'vendor-unspecified',
  radiusMm: null,
  refractiveIndex: null,
  centerThicknessMm: null,
  fillFactor: null,
  pitchToleranceMm: null,
  focalToleranceMm: null,
  radiusToleranceMm: null,
  thicknessToleranceMm: null,
  lateralOffsetMm: 0,
  notes: '',
})

export function CandidatesTab({
  state,
  setState,
  selectedId,
  setSelectedId,
  onGoToRay,
}: CandidatesTabProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [importErrors, setImportErrors] = useState<string[]>([])
  const selected = state.candidates.find((candidate) => candidate.id === selectedId) ?? null
  const evaluations = state.candidates.map((candidate) => ({
    candidate,
    evaluation: evaluateCandidate(candidate, state),
  }))
  const updateCandidate = (patch: Partial<CandidateLens>) => {
    if (!selectedId) return
    setState((current) => ({
      ...current,
      candidates: current.candidates.map((candidate) =>
        candidate.id === selectedId ? { ...candidate, ...patch } : candidate,
      ),
    }))
  }

  const importFile = async (file: File) => {
    const text = await file.text()
    const result = file.name.toLowerCase().endsWith('.json')
      ? parseCandidatesJson(text)
      : parseCandidatesCsv(text)
    if (!result.candidates) {
      setImportErrors(result.errors)
      return
    }
    setImportErrors([])
    setState((current) => ({ ...current, candidates: [...current.candidates, ...result.candidates!] }))
    setSelectedId(result.candidates[0]?.id ?? null)
  }

  const loadSelected = () => {
    if (!selected) return
    setState((current) => ({
      ...current,
      lens: lensConfigurationForCandidate(current, selected),
    }))
    onGoToRay(selected.id)
  }

  return (
    <div className="tab-stack">
      <h1 className="sr-only">Candidate comparison</h1>
      <Section
        title="Candidate comparison"
        description="A v1 engineering screen against enabled checks. It does not certify compatibility, manufacturability, or production performance."
        actions={
          <div className="toolbar">
            <button className="secondary-button" onClick={() => {
              const candidate = blankCandidate()
              setState((current) => ({ ...current, candidates: [...current.candidates, candidate] }))
              setSelectedId(candidate.id)
            }}><Plus size={15} /> Add candidate</button>
            <button className="secondary-button" onClick={() => inputRef.current?.click()}><FileUp size={15} /> Import</button>
            <button className="secondary-button" onClick={() => downloadText('lenticular-candidates.csv', exportCandidatesCsv(state.candidates), 'text/csv')} disabled={!state.candidates.length}><Download size={15} /> CSV</button>
            <button className="secondary-button" onClick={() => downloadText('lenticular-candidates.json', exportCandidatesJson(state.candidates), 'application/json')} disabled={!state.candidates.length}><Download size={15} /> JSON</button>
            <input ref={inputRef} className="sr-only" type="file" aria-label="Import candidate CSV or JSON" accept=".csv,.json,text/csv,application/json" onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void importFile(file)
              event.target.value = ''
            }} />
          </div>
        }
      >
        {importErrors.length ? <Notice tone="danger" title="Import rejected">{importErrors.join(' ')}</Notice> : null}
        {!state.candidates.length ? (
          <EmptyState title="No candidate sheets">Add a sheet manually or import a CSV/JSON list. The required lens remains available as a design target.</EmptyState>
        ) : (
          <HorizontalScrollRegion className="table-scroll tall" label="Candidate comparison table">
            <table className="data-table candidate-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Mode</th>
                  <th>Actual pitch</th>
                  <th>Pitch error</th>
                  <th>Required / actual focal</th>
                  <th>Predicted distance</th>
                  <th>Adjacent spacing</th>
                  <th>Unwrapped drift</th>
                  <th>Screening gaps</th>
                  <th>Analytical screen</th>
                  <th>Simulated XT</th>
                  <th>Robustness</th>
                </tr>
              </thead>
              <tbody>
                {evaluations.map(({ candidate, evaluation }) => (
                  <tr key={candidate.id} className={candidate.id === selectedId ? 'selected-row' : ''} onClick={() => setSelectedId(candidate.id)}>
                    <th scope="row" className="candidate-identity-cell">
                      <button
                        type="button"
                        className="candidate-select-button"
                        onClick={() => setSelectedId(candidate.id)}
                        aria-pressed={candidate.id === selectedId}
                      >
                        {candidate.name}
                      </button>
                      <small>{candidate.manufacturer || 'No source note'}</small>
                      <div className="mobile-candidate-verdict" aria-hidden="true">
                        <span><b>Inputs</b>{evaluation.inputMissing.length ? `${evaluation.inputMissing.length} needed` : 'Ready'}</span>
                        <span><b>Nominal</b>{evaluation.label}</span>
                        <span><b>Trace</b>{evaluation.simulationEvidenceCurrent ? 'Current' : evaluation.simulationEvidenceStale ? 'Stale' : 'Not run'}</span>
                        <span><b>Robustness</b>{evaluation.monteCarloEvidenceCurrent ? 'Current' : evaluation.monteCarloEvidenceStale ? 'Stale' : 'Not run'}</span>
                      </div>
                    </th>
                    <td>{candidate.mode === 'thin' ? 'Thin' : 'Circular thick'}</td>
                    <td>{formatNumber(evaluation.pitchMm, 6)} mm<br /><small>{formatNumber(evaluation.lpi, 3)} LPI</small></td>
                    <td>{formatNumber(evaluation.mismatch?.pitchErrorUm, 4)} µm</td>
                    <td>{formatNumber(evaluation.requiredFocalBehaviorMm, 6)} / {formatNumber(evaluation.focalBehaviorMm, 6)} mm</td>
                    <td>{evaluation.predictedViewingDistance.distanceMm !== null ? `${formatNumber(evaluation.predictedViewingDistance.distanceMm, 1)} mm` : evaluation.predictedViewingDistance.state}</td>
                    <td>{formatNumber(evaluation.predictedSpacingMm, 2)} mm</td>
                    <td>{formatNumber(evaluation.mismatch?.driftSourceCells, 3)} q</td>
                    <td>{evaluation.missing.length ? `${evaluation.missing.length} missing` : 'Complete'}</td>
                    <td><StatusBadge status={evaluation.analyticalStatus} label={evaluation.analyticalLabel} /></td>
                    <td>{evaluation.simulationEvidenceCurrent ? `${formatNumber(candidate.simulated!.crosstalkMax * 100, 1)}%` : evaluation.simulationEvidenceStale ? 'Stale' : 'Not run'}</td>
                    <td>{evaluation.monteCarloEvidenceCurrent ? `${formatNumber(candidate.monteCarlo!.successRate * 100, 1)}% / ${candidate.monteCarlo!.trials}` : evaluation.monteCarloEvidenceStale ? 'Stale' : 'Not run'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HorizontalScrollRegion>
        )}
      </Section>

      {selected ? (
        <Section
          title="Selected candidate"
          description="Enter documented vendor or measured values. Numerical evidence is retained and marked stale when fingerprinted inputs change."
          actions={
            <button className="danger-button" onClick={() => {
              setState((current) => ({ ...current, candidates: current.candidates.filter((candidate) => candidate.id !== selected.id) }))
              setSelectedId(null)
            }}><Trash2 size={15} /> Delete candidate</button>
          }
        >
          <div className="form-grid three">
            <Field label="Candidate name"><input value={selected.name} onChange={(event) => updateCandidate({ name: event.target.value })} /></Field>
            <Field label="Manufacturer / source note"><input value={selected.manufacturer} onChange={(event) => updateCandidate({ manufacturer: event.target.value })} /></Field>
            <SelectField label="Lens mode" value={selected.mode} onChange={(value) => updateCandidate({ mode: value as CandidateLens['mode'] })} options={[{ value: 'thin', label: 'Ideal thin-lens specification' }, { value: 'thick', label: 'Circular plano-convex geometry' }]} />
            <NumberField label="Pitch" unit="mm" value={selected.pitchMm} onChange={(value) => updateCandidate({ pitchMm: value, lpi: syncLpiFromPitch(value) })} min={0} />
            <NumberField label="LPI" value={selected.lpi} onChange={(value) => updateCandidate({ lpi: value, pitchMm: syncPitchFromLpi(value) })} min={0} />
            <NumberField label="Fill factor" unit="0–1" value={selected.fillFactor} onChange={(value) => updateCandidate({ fillFactor: value })} min={0} max={1} />
            {selected.mode === 'thin' ? (
              <>
                <NumberField label="Focal length / BFL" unit="mm" value={selected.focalLengthMm} onChange={(value) => updateCandidate({ focalLengthMm: value })} min={0} />
                <SelectField label="Focal reference" value={selected.focalReference} onChange={(value) => updateCandidate({ focalReference: value as CandidateLens['focalReference'] })} options={[
                  { value: 'effective-at-plane', label: 'EFL at ideal lens plane' },
                  { value: 'back-from-plane', label: 'BFL from ideal lens plane' },
                  { value: 'vendor-unspecified', label: 'Vendor reference unspecified' },
                ]} />
                <NumberField label="Focal tolerance" unit="mm σ / ±" value={selected.focalToleranceMm} onChange={(value) => updateCandidate({ focalToleranceMm: value })} min={0} />
              </>
            ) : (
              <>
                <NumberField label="Radius" unit="mm" value={selected.radiusMm} onChange={(value) => updateCandidate({ radiusMm: value })} min={0} />
                <NumberField label="Lens index" value={selected.refractiveIndex} onChange={(value) => updateCandidate({ refractiveIndex: value })} min={1} />
                <NumberField label="Center thickness" unit="mm" value={selected.centerThicknessMm} onChange={(value) => updateCandidate({ centerThicknessMm: value })} min={0} />
                <NumberField label="Radius tolerance" unit="mm σ / ±" value={selected.radiusToleranceMm} onChange={(value) => updateCandidate({ radiusToleranceMm: value })} min={0} />
                <NumberField label="Thickness tolerance" unit="mm σ / ±" value={selected.thicknessToleranceMm} onChange={(value) => updateCandidate({ thicknessToleranceMm: value })} min={0} />
              </>
            )}
            <NumberField label="Pitch tolerance" unit="mm σ / ±" value={selected.pitchToleranceMm} onChange={(value) => updateCandidate({ pitchToleranceMm: value })} min={0} />
            <NumberField label="Lateral phase" unit="mm" value={selected.lateralOffsetMm} onChange={(value) => updateCandidate({ lateralOffsetMm: value })} />
            <Field label="Notes"><textarea value={selected.notes} onChange={(event) => updateCandidate({ notes: event.target.value })} rows={3} /></Field>
          </div>

          {(() => {
            const evaluation = evaluateCandidate(selected, state)
            return (
              <div className="candidate-evaluation">
                <div className="evaluation-heading">
                  <div><ModelBadge>enabled nominal v1 checks</ModelBadge><h3>Screening details</h3></div>
                  <StatusBadge status={evaluation.status} label={evaluation.label} />
                </div>
                <div className="metric-grid compact-top">
                  <Metric label="Required focal behavior" value={formatNumber(evaluation.requiredFocalBehaviorMm, 6)} unit="mm" note={selected.mode === 'thick' ? 'EFL required at numerical principal plane' : 'Finite-conjugate thin-lens target'} />
                  <Metric label="Actual focal behavior" value={formatNumber(evaluation.focalBehaviorMm, 6)} unit="mm" note={evaluation.focalBehaviorLabel} />
                  <Metric label="Focal behavior error" value={formatNumber(evaluation.focalBehaviorErrorMm, 6)} unit="mm" />
                  <Metric label="Predicted adjacent spacing" value={formatNumber(evaluation.predictedSpacingMm, 3)} unit="mm" note="Analytical first-order estimate" />
                  <Metric label="Predicted viewing distance" value={formatNumber(evaluation.predictedViewingDistance.distanceMm, 2)} unit={evaluation.predictedViewingDistance.distanceMm !== null ? 'mm' : undefined} note={evaluation.predictedViewingDistance.message} />
                </div>
                {evaluation.reasons.length ? <ul className="reason-list">{evaluation.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul> : null}
                {evaluation.inputMissing.length ? <Notice tone="warning" title="Inputs needed">{evaluation.inputMissing.join(', ')}.</Notice> : null}
                {evaluation.evidenceMissing.length ? <Notice tone="warning" title="Evidence needed">{evaluation.evidenceMissing.join(', ')}.</Notice> : null}
              </div>
            )
          })()}

          <div className="action-row">
            <button className="primary-button" onClick={loadSelected}><Play size={15} /> Load candidate into ray simulation</button>
            <p className="action-note">Successful trace and tolerance runs are saved to this loaded candidate automatically.</p>
          </div>
        </Section>
      ) : null}
    </div>
  )
}
